import React, { type JSX, type ReactNode } from "react";
import { pickAdminDashboardLocaleText } from "../locale/admin-dashboard-copy";
import { useAdminDashboardLocale } from "../locale/admin-dashboard-locale-context";
import type {
  AdminOperatorExplanationScreen,
  AdminOperatorExplanationSlot,
} from "../locale/admin-operator-explanations";
import { AdminOperatorExplanationBlock } from "./admin-operator-explanation-block";
import type { AdminStateMetaItem } from "./admin-empty-state";

export type AdminErrorStateProps = {
  action?: ReactNode;
  body?: string;
  guidance?: {
    screen: AdminOperatorExplanationScreen;
    slot: AdminOperatorExplanationSlot;
  };
  meta?: AdminStateMetaItem[];
  requestId?: string;
  title: string;
  tone?: "error" | "permission_denied" | "source_unavailable" | "redaction_failed";
};

function resolveToneConfig(
  tone: AdminErrorStateProps["tone"],
  locale: "ko" | "en",
): { background: string; border: string; label: string; textColor: string; bodyColor: string } {
  if (tone === "permission_denied") {
    return {
      label: pickAdminDashboardLocaleText(locale, {
        en: "PERMISSION DENIED",
        ko: "권한 거부",
      }),
      textColor: "#7f1d1d",
      bodyColor: "#7f1d1d",
      border: "1px solid rgba(185, 28, 28, 0.18)",
      background: "rgba(254, 242, 242, 0.88)",
    };
  }

  if (tone === "source_unavailable") {
    return {
      label: pickAdminDashboardLocaleText(locale, {
        en: "SOURCE UNAVAILABLE",
        ko: "소스 사용 불가",
      }),
      textColor: "#9a3412",
      bodyColor: "#9a3412",
      border: "1px solid rgba(217, 119, 6, 0.2)",
      background: "rgba(255, 247, 237, 0.92)",
    };
  }

  if (tone === "redaction_failed") {
    return {
      label: pickAdminDashboardLocaleText(locale, {
        en: "REDACTION FAILED",
        ko: "비식별화 실패",
      }),
      textColor: "#991b1b",
      bodyColor: "#7f1d1d",
      border: "1px solid rgba(220, 38, 38, 0.24)",
      background: "rgba(254, 242, 242, 0.92)",
    };
  }

  return {
    label: pickAdminDashboardLocaleText(locale, { en: "ERROR", ko: "오류" }),
    textColor: "#b91c1c",
    bodyColor: "#7f1d1d",
    border: "1px solid rgba(220, 38, 38, 0.16)",
    background: "rgba(254, 242, 242, 0.84)",
  };
}

export function AdminErrorState(props: AdminErrorStateProps): JSX.Element {
  const { locale } = useAdminDashboardLocale();
  const tone = resolveToneConfig(props.tone, locale);

  return (
    <section
      style={{
        padding: "24px",
        borderRadius: "20px",
        border: tone.border,
        background: tone.background,
      }}
    >
      <p style={{ margin: 0, color: tone.textColor, fontSize: "0.78rem", fontWeight: 700 }}>
        {tone.label}
      </p>
      <h3 style={{ margin: "8px 0 0", fontSize: "1.1rem", letterSpacing: "-0.02em" }}>{props.title}</h3>
      {props.body ? (
        <p style={{ margin: "8px 0 0", color: tone.bodyColor, lineHeight: 1.5 }}>{props.body}</p>
      ) : null}
      {props.requestId ? (
        <p style={{ margin: "12px 0 0", color: tone.textColor, fontSize: "0.88rem" }}>
          {pickAdminDashboardLocaleText(locale, { en: "Request", ko: "요청" })} {props.requestId}
        </p>
      ) : null}
      {props.guidance ? (
        <AdminOperatorExplanationBlock
          screen={props.guidance.screen}
          slot={props.guidance.slot}
          style={{ marginTop: "14px" }}
        />
      ) : null}
      {props.meta && props.meta.length > 0 ? (
        <dl
          style={{
            margin: "16px 0 0",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "12px",
          }}
        >
          {props.meta.map((item) => (
            <div key={item.key}>
              <dt
                style={{
                  margin: 0,
                  color: "#7f1d1d",
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                {item.label}
              </dt>
              <dd style={{ margin: "4px 0 0", color: "#18212b", fontSize: "0.92rem" }}>{item.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {props.action ? <div style={{ marginTop: "16px" }}>{props.action}</div> : null}
    </section>
  );
}
